import React, { useCallback, useMemo, useState } from 'react';
import { Image, Modal, Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import {
	CHECKLIST_ITEM_TYPES,
	formatChecklistAnswerValue,
	getVisibleChecklistItems,
} from '../../../common/utils/checklist/checklistItems';

import { Text } from '../../components';
import { ImageViewer } from '../../components/image';
import { SectionTitle } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';

// Longer answers go under their label so they read as text, not as a narrow column.
const MAX_INLINE_ANSWER_LENGTH = 24;

/**
 * Read-only summary of a submitted checklist: each answer beside or under its
 * label, flagged answers marked, the driver's comments, and photos that open
 * full screen. Props: items (template items), answers ([{ itemId, value, comment }]),
 * flaggedItemIds (array of item ids).
 */
const ChecklistAnswerSummary = ({ items, answers, flaggedItemIds }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [viewer, setViewer] = useState(null);

	const visibleItems = useMemo(() => getVisibleChecklistItems(items || [], answers || []), [answers, items]);
	const answersByItemId = useMemo(() => (
		new Map((answers || []).map(answer => [answer.itemId, answer]))
	), [answers]);
	const flaggedIds = useMemo(() => new Set(flaggedItemIds || []), [flaggedItemIds]);
	const closeViewer = useCallback(() => setViewer(null), []);

	return (
		<View style={styles.list}>
			{
				visibleItems.map((item) => {
					if (item.type === CHECKLIST_ITEM_TYPES.HEADING) {
						return <SectionTitle key={item.id} title={item.label} />;
					}

					const answer = answersByItemId.get(item.id);
					const isFlagged = flaggedIds.has(item.id);
					const photoLinks = item.type === CHECKLIST_ITEM_TYPES.PHOTO && Array.isArray(answer?.value)
						? answer.value
						: null;
					const valueText = photoLinks
						? (!photoLinks.length && 'No photo') || null
						: formatChecklistAnswerValue(item, answer);
					const isStacked = item.type === CHECKLIST_ITEM_TYPES.TEXT ||
						String(valueText || '').length > MAX_INLINE_ANSWER_LENGTH;

					return (
						<View key={item.id} style={[styles.itemBlock, isFlagged && styles.itemBlockFlagged]}>
							<View style={[styles.itemRow, isStacked && styles.itemRowStacked]}>
								<View style={styles.labelRow}>
									{isFlagged && <Feather name="flag" size={16} color={colors.danger} accessibilityLabel="Flagged" />}
									<Text style={styles.itemLabel}>{item.label}</Text>
								</View>
								{
									Boolean(valueText) && (
										<Text style={isStacked ? styles.itemValueStacked : styles.itemValue}>{valueText}</Text>
									)
								}
							</View>
							{
								Boolean(photoLinks?.length) && (
									<View style={styles.photoRow}>
										{photoLinks.map((link, index) => (
											<Pressable
												key={link}
												onPress={() => setViewer({ links: photoLinks, index })}
												accessibilityRole="imagebutton"
												accessibilityLabel={`Open photo ${index + 1} of ${item.label}`}
											>
												<Image source={{ uri: link }} style={styles.photo} />
											</Pressable>
										))}
									</View>
								)
							}
							{Boolean(answer?.comment) && <Text style={styles.itemComment}>{answer.comment}</Text>}
						</View>
					);
				})
			}
			{
				Boolean(viewer) && (
					<Modal visible={true} transparent={true} onRequestClose={closeViewer}>
						<ImageViewer
							close={closeViewer}
							index={viewer.index}
							imageUrls={viewer.links.map(url => ({ url }))}
						/>
					</Modal>
				)
			}
		</View>
	);
};

const createStyles = ({ ui: { colors, radius, space, type } }) => ({
	list: {
		gap: space.sm,
	},
	itemBlock: {
		gap: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		borderRadius: radius.card,
		borderWidth: 1,
		borderColor: colors.raisedBorder,
		backgroundColor: colors.raised,
	},
	// Flagged answers carry a danger bar and flag, like the classic page and the web dialog.
	itemBlockFlagged: {
		borderLeftWidth: 4,
		borderLeftColor: colors.danger,
	},
	itemRow: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		gap: space.md,
	},
	itemRowStacked: {
		flexDirection: 'column',
		gap: space.xs,
	},
	labelRow: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.xs,
		flexShrink: 1,
	},
	itemLabel: {
		...type.rowText,
		color: colors.text,
		flexShrink: 1,
	},
	itemValue: {
		...type.rowTitleSemibold,
		color: colors.text,
		maxWidth: '45%',
		textAlign: 'right',
	},
	itemValueStacked: {
		...type.bodySmallSemibold,
		color: colors.text,
	},
	itemComment: {
		...type.secondary,
		color: colors.textSecondary,
	},
	photoRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	photo: {
		width: 96,
		height: 96,
		borderRadius: radius.chip,
		backgroundColor: colors.card,
	},
});

export default ChecklistAnswerSummary;
